import { site } from "../lib/siteConfig";
import { Phone, Mail, Instagram, Globe, MessageCircle, Search, Compass, Sparkles, Users, TrendingUp } from "lucide-react";

const QR = ({ data, size = 180, testid }) => (
  <img
    data-testid={testid}
    alt="QR Code"
    src={`https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&format=png&margin=6&qzone=1&data=${encodeURIComponent(data)}`}
    className="w-full h-full object-contain"
  />
);

// Case study data
const cases = [
  {
    address: "Via Tonale",
    breve: "950",
    trans: "1.300",
    photo: "https://customer-assets.emergentagent.com/job_transitorio-milano/artifacts/9ftcujlm_259A1689-HDR.jpg",
  },
  {
    address: "Piazza Firenze",
    breve: "700",
    trans: "1.200",
    photo: "https://customer-assets.emergentagent.com/job_transitorio-milano/artifacts/zsub51me_R5__8834-Edit-Edit.jpg",
  },
];

const ContactBar = ({ variant = "light" }) => {
  const dark = variant === "dark";
  return (
    <div
      className={`px-8 py-2 ${
        dark ? "bg-brand-ink text-white" : "bg-brand-beige/60 text-brand-ink"
      }`}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div
            className={`text-[9px] uppercase tracking-[0.28em] font-semibold ${
              dark ? "text-brand-beige" : "text-brand-muted"
            }`}
          >
            Francesca Gorla · Consulente Immobiliare
          </div>
          <div className={`mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-[10px] ${dark ? "text-white/90" : "text-brand-ink"}`}>
            <div className="flex items-center gap-1.5">
              <MessageCircle size={11} className={dark ? "text-brand-beige" : "text-[#25D366]"} />
              <span className="font-semibold">{site.whatsappNumber}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Globe size={11} className={dark ? "text-brand-beige" : "text-brand-ink"} />
              <span className="font-semibold">gorlaproperties.com</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Instagram size={11} className={dark ? "text-brand-beige" : "text-brand-ink"} />
              <span className="text-[9px]">{site.instagramHandle}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Mail size={11} className={dark ? "text-brand-beige" : "text-brand-ink"} />
              <span className="text-[9px] break-all">{site.email}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Front = () => (
  <section className="page flyer-page flex flex-col overflow-hidden bg-brand-bg">
    <header className="flex items-center justify-between px-8 pt-7">
      <div>
        <div className="font-display text-xl leading-none text-brand-ink">
          Gorla<span className="italic font-normal"> Properties</span>
        </div>
        <div className="mt-1 text-[8px] uppercase tracking-[0.28em] text-brand-muted">
          Consulenza · Rendita immobiliare
        </div>
      </div>
      <div className="text-right">
      </div>
    </header>

    {/* Headline */}
    <div className="px-8 mt-3">
      <h1 className="font-display text-[1.9rem] leading-[1.02] font-black text-brand-ink">
        Sei sicuro che stai davvero <span className="italic font-normal">guadagnando</span> con il tuo immobile?
      </h1>
      <p className="mt-4 text-[11.5px] leading-relaxed text-brand-muted">
        Il mercato degli affitti brevi sta restituendo meno di quanto pensi. Commissioni OTA, utenze, pulizie e property manager erodono il margine — e la domanda si è già spostata verso il <span className="text-brand-ink font-medium">transitorio</span>.
      </p>
    </div>

    {/* Trust badges */}
    <div className="mx-8 mt-3 grid grid-cols-3 gap-2.5">
      {[
        { k: "10%", v: "Cedolare secca*" },
        { k: "18 mesi", v: "Durata massima" },
        { k: "IMU", v: "Sconto applicato*" },
      ].map((s) => (
        <div key={s.v} className="bg-white border border-brand-line rounded-sm px-3 py-2.5 text-center">
          <div className="font-display text-lg font-bold text-brand-ink leading-none">{s.k}</div>
          <div className="mt-1 text-[7.5px] uppercase tracking-[0.14em] text-brand-muted">{s.v}</div>
        </div>
      ))}
    </div>
    <p className="mx-8 mt-1.5 text-[7.5px] italic text-brand-muted hidden">*Solo per contratti a canone concordato.</p>

    {/* Highlight transitorio */}
    <div className="mx-8 mt-3 bg-brand-ink text-white p-3 rounded-sm">
      <div className="text-[8px] uppercase tracking-[0.28em] text-brand-beige font-semibold">
        La soluzione oggi
      </div>
      <div className="mt-1.5 font-display text-[14px] leading-tight font-bold">
        L'affitto transitorio.
      </div>
      <div className="mt-2 text-[10px] leading-snug text-white/85">
        Rendita netta più alta · pagamenti sicuri · minori costi di gestione · fiscalità agevolata.
      </div>
    </div>

    {/* Case studies */}
    <div className="mx-8 mt-4">
      <div className="text-[9px] uppercase tracking-[0.22em] font-semibold text-brand-ink mb-2 flex items-center gap-2">
        <TrendingUp size={11} /> Casi reali · confronto rendita netta
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        {cases.map((c) => (
          <div key={c.address} className="bg-white border border-brand-line rounded-sm overflow-hidden">
            <div className="relative aspect-[16/9] overflow-hidden bg-brand-beige/30">
              <img src={c.photo} alt={c.address} className="w-full h-full object-cover" />
            </div>
            <div className="px-3 py-2.5">
              <div className="font-display text-[11px] font-semibold text-brand-ink leading-tight">
                {c.address}
              </div>
              <div className="mt-1.5 flex items-center justify-between gap-2 text-[9px] leading-tight">
                <div>
                  <div className="text-brand-muted uppercase tracking-[0.1em] text-[7px]">Breve</div>
                  <div className="text-brand-muted line-through leading-none">€ {c.breve}</div>
                  <div className="text-[7px] text-brand-muted mt-0.5">netti/mese</div>
                </div>
                <div className="text-brand-ink text-base font-black leading-none">→</div>
                <div className="text-right">
                  <div className="text-brand-ink uppercase tracking-[0.1em] text-[7px] font-semibold">Transitorio</div>
                  <div className="text-brand-ink font-display text-[13px] font-bold leading-none">
                    € {c.trans}
                  </div>
                  <div className="text-[7px] text-brand-muted mt-0.5">netti/mese</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Unified dark CTA + Contact block */}
    <div className="mt-auto bg-brand-ink text-white px-8 py-5">
      <div className="flex items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="font-display text-[16px] font-bold leading-tight">
            Parliamo del tuo immobile.
          </div>
          <div className="mt-2 text-[9px] uppercase tracking-[0.22em] text-brand-beige font-semibold">
            Francesca Gorla · Consulente Immobiliare Abilitata
          </div>
          <div className="mt-2 space-y-0.5 text-[11px] text-white/90">
            <div className="flex items-center gap-1.5">
              <MessageCircle size={11} className="text-brand-beige" />
              <span className="font-semibold">{site.whatsappNumber}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Mail size={11} className="text-brand-beige" />
              <span>{site.email}</span>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-center gap-1 shrink-0">
          <div className="bg-white p-1.5 rounded-sm w-[82px] h-[82px]">
            <QR data={site.whatsappUrl} size={200} testid="qr-whatsapp-front" />
          </div>
          <div className="text-[7px] uppercase tracking-[0.2em] text-brand-beige">WhatsApp</div>
        </div>
      </div>
    </div>
  </section>
);

const Back = () => {
  const steps = [
    { icon: Search, t: "Analizzo il portafoglio immobiliare", d: "Posizione, caratteristiche, potenziale reale di mercato." },
    { icon: Compass, t: "Definiamo la strategia", d: "Fiscale e di redditività: la formula che rende di più." },
    { icon: Sparkles, t: "Gestisco burocrazia e operatività", d: "Preparo l'immobile e ne curo l'intera fase operativa." },
    { icon: Users, t: "Individuo clienti a target", d: "Inquilini o acquirenti in linea con i tuoi obiettivi." },
  ];
  const scenarios = [
    { k: "Affitto Breve", d: "Analisi mercato · Annunci · Gestione operativa e burocratica.", tone: "beige" },
    { k: "Affitto Transitorio", d: "Fino a 18 mesi · Cedolare 10%* · Sconto IMU.", tone: "dark" },
    { k: "Affitto 4+4", d: "Soluzione stabile e a lungo termine, minori incombenze.", tone: "beige" },
    { k: "Vendita", d: "Disinvestire per reinvestire con consapevolezza.", tone: "dark" },
  ];
  return (
    <section className="page flyer-page flex flex-col overflow-hidden bg-white">
      <header className="px-8 pt-7 relative">
        <img
          src="https://customer-assets.emergentagent.com/job_transitorio-milano/artifacts/qb7b5d8h_ChatGPT%20Image%2022%20apr%202026%2C%2015_00_21.png"
          alt="Francesca Gorla"
          className="absolute top-10 right-10 w-[110px] h-[110px] object-cover rounded-full border-[3px] border-brand-beigeDark shadow-lg"
        />
        <div className="text-[9px] uppercase tracking-[0.28em] font-semibold text-brand-ink flex items-center gap-2">
          <span className="h-px w-8 bg-brand-beigeDark" /> Chi sono
        </div>
        <h2 className="mt-3 font-display text-[1.7rem] leading-tight font-bold text-brand-ink pr-[130px]">
          Non sono un agente.<br />
          <span className="italic font-normal">Sono la tua consulente.</span>
        </h2>
      </header>

      {/* Method */}
      <div className="px-8 mt-5">
        <div className="text-[9px] uppercase tracking-[0.22em] font-semibold text-brand-ink mb-3">
          Il mio metodo
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          {steps.map((s) => (
            <div key={s.t} className="bg-brand-bg border border-brand-line rounded-sm p-3">
              <div className="flex items-center gap-2">
                <div className="h-6 w-6 flex items-center justify-center border border-brand-beigeDark text-brand-ink rounded-sm">
                  <s.icon size={11} strokeWidth={1.8} />
                </div>
                <div className="font-display text-[11px] font-semibold text-brand-ink leading-tight">{s.t}</div>
              </div>
              <p className="mt-1.5 text-[9px] text-brand-muted leading-snug">{s.d}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Scenarios stacked, alternating colors */}
      <div className="px-8 mt-5">
        <div className="text-[9px] uppercase tracking-[0.22em] font-semibold text-brand-ink mb-3">
          Cosa posso fare per te
        </div>
        <div className="space-y-2">
          {scenarios.map((s) => {
            const dark = s.tone === "dark";
            return (
              <div
                key={s.k}
                className={`p-3 rounded-sm flex items-start gap-3 ${
                  dark
                    ? "bg-brand-ink text-white"
                    : "bg-brand-beige/60 text-brand-ink"
                }`}
              >
                <div className={`font-display text-[12px] font-semibold w-[120px] shrink-0 ${dark ? "text-white" : "text-brand-ink"}`}>
                  {s.k}
                </div>
                <div className={`text-[10px] leading-snug ${dark ? "text-white/85" : "text-brand-muted"}`}>
                  {s.d}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* QR + Contact banner */}
      <div className="mt-auto">
        <div className="bg-brand-ink text-white px-8 py-5">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="text-[9px] uppercase tracking-[0.28em] text-brand-beige font-semibold">
                Parliamo del tuo immobile
              </div>
              <div className="mt-1 font-display text-[16px] font-bold">Francesca Gorla</div>
              <div className="mt-2 space-y-0.5 text-[10px]">
                <div className="flex items-center gap-1.5">
                  <Phone size={10} /> {site.whatsappNumber}
                </div>
                <div className="flex items-center gap-1.5">
                  <Mail size={10} /> {site.email}
                </div>
                <div className="flex items-center gap-1.5">
                  <Globe size={10} /> gorlaproperties.com
                </div>
                <div className="flex items-center gap-1.5">
                  <Instagram size={10} /> {site.instagramHandle}
                </div>
              </div>
            </div>
            <div className="flex flex-col items-center gap-1.5 shrink-0">
              <div className="bg-white p-1.5 rounded-sm w-[78px] h-[78px]">
                <QR data="https://gorlaproperties.com" testid="qr-back-site" />
              </div>
              <div className="text-[7px] uppercase tracking-[0.2em] text-brand-beige">Sito</div>
            </div>
          </div>
          <div className="mt-4 pt-3 border-t border-white/15 flex items-center justify-between text-[8px] uppercase tracking-[0.18em] text-white/50">
            <span>{site.rea}</span>
            <span>{site.vat}</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export const Flyer = () => {
  return (
    <div data-testid="flyer-container" className="flyer-viewport">
      <style>{`
        @page { size: A5; margin: 0; }
        @media print {
          html, body { background: #fff; }
          .flyer-controls { display: none !important; }
          .flyer-viewport {
            background: #fff !important;
            padding: 0 !important;
            gap: 0 !important;
          }
          .flyer-page {
            box-shadow: none !important;
            margin: 0 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          .flyer-page * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          .page-break { display: none !important; }
        }
        .flyer-page, .flyer-page * {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
          color-adjust: exact;
        }
        .flyer-viewport {
          min-height: 100vh;
          background: #f0eee8;
          padding: 32px 16px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 32px;
        }
        .flyer-page {
          width: 148mm;
          height: 210mm;
          box-shadow: 0 20px 40px rgba(0,0,0,0.08);
          position: relative;
        }
        .flyer-page:not(:last-of-type) {
          break-after: page;
          page-break-after: always;
        }
      `}</style>

      <div className="flyer-controls max-w-[148mm] w-full flex items-center justify-between gap-3">
        <a
          href="/"
          data-testid="flyer-back"
          className="text-sm text-brand-muted hover:text-brand-ink transition-colors"
        >
          ← Torna al sito
        </a>
        <button
          type="button"
          onClick={() => window.print()}
          data-testid="flyer-print"
          className="inline-flex items-center gap-2 bg-brand-ink text-white text-sm font-medium px-5 py-3 rounded-sm hover:bg-brand-inkHover transition-colors"
        >
          Stampa · Salva come PDF
        </button>
      </div>

      <Front />
      <div className="page-break" />
      <Back />

      <p className="flyer-controls text-xs text-brand-muted text-center max-w-[148mm]">
        Suggerimento: usa Chrome o Safari → Stampa → Salva come PDF · Formato A5 · Senza margini · Attiva "Grafica di sfondo".
      </p>
    </div>
  );
};
